
import BinVertexShader from '../../shaders/bin/bin.vert';
import BinFragmentShader from '../../shaders/bin/bin.frag';

import { LineTypes, Palettes } from './SBPVisualizer';

export default class SBPBin extends THREE.Object3D {
  constructor (width, depth, thickness, texture, diffuse, xOffset, yOffset) {
    super();

    this._t = 1;
    this.width = width;
    this.depth = depth;
    this.thickness = thickness;

    // offset from kijun (0,0,0)
    this.xOffset = xOffset;
    this.yOffset = yOffset;

    this.add(this.buildContourMesh(width, depth, thickness, texture, xOffset, yOffset));

    const geom = new THREE.PlaneGeometry(width, depth, 1, 1);
    const layers = thickness; // 7
    const ht = this.thickness * 0.5;
    const offset = 1e-3; // offset for numerical error bug
    for (let i = 0; i < layers; i++) {
      const t = i / (layers - 1) + offset;

      const uniforms = THREE.UniformsUtils.merge([
        THREE.UniformsLib.common,
        THREE.UniformsLib.envmap,
        THREE.UniformsLib.normalmap,
        // THREE.UniformsLib.roughnessmap,
        THREE.UniformsLib.lights,
        {
          diffuse: { value: diffuse },
          emissive: { value: new THREE.Color(0x000000) },
          roughness: { value: texture.roughness },
          metalness: { value: texture.metalness },
          envMapIntensity: { value: 1 },
          tClip: { value: null },
          pLayer: { value: t },
          mColor: { value: Palettes[LineTypes.MOVE].color },
          jColor: { value: Palettes[LineTypes.JOG].color },
          pColor: { value: Palettes[LineTypes.POCKET].color },
        }
      ]);
      uniforms.map.value = texture.map;
      uniforms.normalMap.value = texture.normalMap;
      // uniforms.roughnessMap.value = texture.roughnessMap;

      const mesh = new THREE.Mesh(
        geom,
        new THREE.ShaderMaterial({
          lights: true,
          extensions: {
          },
          defines: {
            STANDARD: '',
            USE_MAP: '',
            USE_NORMALMAP: '',
            USE_ENVMAP: '',
            ENVMAP_TYPE_CUBE: '',
            ENVMAP_MODE_REFRACTION: '',
            USE_TANGENT: '',
            OBJECTSPACE_NORMALMAP: '',
            USE_UV: ''
          },
          uniforms: uniforms,
          vertexShader: BinVertexShader,
          fragmentShader: BinFragmentShader,
          side: THREE.DoubleSide
        })
      );
      mesh.position.set(xOffset, yOffset, t * this.thickness - ht);
      this.add(mesh);
    }
  }

  get t () {
    return this._t;
  }

  set t (v) {
    this._t = v;
    this.depthScene.children.forEach(mesh => {
      let uniforms = mesh.material.uniforms;
      if (uniforms !== undefined && uniforms.t !== undefined) {
        uniforms.t.value = this._t;
      }
    });
  }

  buildContourMesh (width, depth, thickness, texture, xOffset, yOffset) {
    const geometry = new THREE.Geometry();

    const hw = width * 0.5;
    const hd = depth * 0.5;
    const ht = thickness * 0.5;

    geometry.vertices.push(
      new THREE.Vector3(-hw + xOffset, -hd + yOffset, -ht),
      new THREE.Vector3(-hw + xOffset, hd + yOffset, -ht),
      new THREE.Vector3(hw + xOffset, hd + yOffset, -ht),
      new THREE.Vector3(hw + xOffset, -hd + yOffset, -ht),
      new THREE.Vector3(-hw + xOffset, -hd + yOffset, ht),
      new THREE.Vector3(-hw + xOffset, hd + yOffset, ht),
      new THREE.Vector3(hw + xOffset, hd + yOffset, ht),
      new THREE.Vector3(hw + xOffset, -hd + yOffset, ht)
    );

    const left = new THREE.Vector3(-1, 0, 0);
    const right = new THREE.Vector3(1, 0, 0);
    const forward = new THREE.Vector3(0, 1, 0);
    const back = new THREE.Vector3(0, -1, 0);
    const faces = [
      new THREE.Face3(0, 4, 1, left), new THREE.Face3(5, 1, 4, left), // left
      new THREE.Face3(1, 5, 2, forward), new THREE.Face3(6, 2, 5, forward), // forward
      new THREE.Face3(2, 6, 3, right), new THREE.Face3(7, 3, 6, right), // right
      new THREE.Face3(3, 7, 0, back), new THREE.Face3(4, 0, 7, back) // back
    ];

    const xAxis = new THREE.Vector3(1, 0, 0);
    const yAxis = new THREE.Vector3(0, 1, 0);
    const zAxis = new THREE.Vector3(0, 0, -1);
    const uvScale = 1 / 4;
    faces.forEach((face, idx) => {
      geometry.faces.push(face);
      // console.log(f.a, f.b, f.c)

      const va = geometry.vertices[face.a];
      const vb = geometry.vertices[face.b];
      const vc = geometry.vertices[face.c];
      let pa = va.clone().projectOnPlane(face.normal);
      let pb = vb.clone().projectOnPlane(face.normal);
      let pc = vc.clone().projectOnPlane(face.normal);

      let axis = zAxis;
      if (Math.abs(face.normal.dot(xAxis)) >= 0.99) {
        axis = yAxis;
      }
      const dx = (new THREE.Vector3()).crossVectors(face.normal, axis);
      const dy = (new THREE.Vector3()).crossVectors(face.normal, dx);
      const uvs = [
        new THREE.Vector2(dx.dot(pa), dy.dot(pa)).multiplyScalar(uvScale),
        new THREE.Vector2(dx.dot(pb), dy.dot(pb)).multiplyScalar(uvScale),
        new THREE.Vector2(dx.dot(pc), dy.dot(pc)).multiplyScalar(uvScale)
      ];

      geometry.faceVertexUvs[0][idx] = uvs;
    });

    geometry.computeBoundingSphere();

    const mesh = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({
      roughness: 0.47,
      metalness: 0.0,
      map: texture.map,
      normalMap: texture.normalMap
      // side: THREE.DoubleSide
    }));
    //mesh.position.set(xOffset + width * 0.5, yOffset + depth * 0.5, 0);
    return mesh;
  }

  setup (renderer, sbpBinLine, sbpBinScrew) {
    this.depthBuffer = new THREE.WebGLRenderTarget(
      // this.width, this.depth,
      Math.min(this.width * 2, 4096), Math.min(this.depth * 2, 4096),
      // 4096, 2048,
      {
        format: THREE.RGBAFormat,
        // type: THREE.UnsignedByteType,
        // type: THREE.HalfFloatType,
        // type: THREE.FloatType,
        stencilBuffer: true,
        depthBuffer: true,
        wrapS: THREE.ClampToEdgeWrapping,
        wrapT: THREE.ClampToEdgeWrapping
      }
    );

    const boundary = new THREE.Box3().setFromObject(sbpBinLine);
    const depthCamera = new THREE.OrthographicCamera(0, this.width, this.depth, 0, 0.1, 100);
    depthCamera.position.set(0, 0, boundary.max.z + this.thickness);

    const depthScene = new THREE.Scene();

    // SBPBinLineとSBPScrewをdepthFunc: GreaterEqualDepthでレンダリングするために、
    // カメラから見て一番手前に衝立を置いてdepthを初期化する
    // depthFunc: GreaterEqualDepthでレンダリングするのは、
    // ツールパステクスチャを板に貼り付けるときに一番深く削られた箇所を可視化したいため
    const nearScreen = new THREE.Mesh(
      new THREE.PlaneGeometry(this.width, this.depth, 1, 1),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(0x000000)
      })
    );
    nearScreen.position.set(this.width * 0.5, this.depth * 0.5, boundary.max.z + this.thickness * 0.5);

    depthScene.add(nearScreen);
    depthScene.add(sbpBinLine);
    depthScene.add(sbpBinScrew);

    sbpBinLine.position.set(-this.xOffset, -this.yOffset, 0);

    nearScreen.renderOrder = 0;
    sbpBinLine.renderOrder = 1;
    sbpBinScrew.renderOrder = 2;

    sbpBinLine.updateMatrixWorld(true);
    sbpBinScrew.updateMatrixWorld(true);
    // sbpBinLine.setResolution(this.depthBuffer.width, this.depthBuffer.height)

    this.depthCamera = depthCamera;
    this.depthScene = depthScene;

    this.render(renderer);

    for (let i in this.children) {
      const mesh = this.children[i];
      if (mesh.material.uniforms !== undefined) {
        mesh.material.uniforms.tClip.value = this.depthBuffer.texture;
      }
    }
  }

  render (renderer) {
    renderer.setRenderTarget(this.depthBuffer);
    renderer.clear(true);
    renderer.render(this.depthScene, this.depthCamera);
    renderer.setRenderTarget(null);

    // renderer.clear(true); renderer.render(this.depthScene, this.depthCamera);
  }
}



// WEBPACK FOOTER //
// ./src/assets/javascripts/sbp/SBPBin.js